import { useState, useEffect, useRef } from 'react';
import AnimatedHeading from '@/components/AnimatedHeading';

interface SectionHeadingProps {
  title: string;
  description?: string;
  align?: 'left' | 'center';
  className?: string;
}

export default function SectionHeading({
  title,
  description,
  align = 'left',
  className = '',
}: SectionHeadingProps) {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const alignClass =
    align === 'center'
      ? 'text-center mx-auto items-center'
      : 'text-left items-start';

  return (
    <div ref={containerRef} className={`flex flex-col ${alignClass} max-w-3xl ${className}`}>
      <AnimatedHeading
        text={title}
        animate={isVisible}
        align={align}
        className="font-display text-3xl md:text-5xl lg:text-6xl font-normal leading-[1.05] cursor-default text-balance"
        charDelay={30}
      />
      {description && (
        <p className={`gsap-reveal mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-2xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-4 blur-sm'}`}>
          {description}
        </p>
      )}
    </div>
  );
}